/**
 * Custom Animation System
 * Replacement for AOS (Animate On Scroll) library
 * Uses Intersection Observer API for better performance
 *
 * ANIMATION BEHAVIOR: One-way animations only (animate-in, never animate-out)
 * Elements fade up and in when entering viewport, then stay in their final state permanently
 */

class AnimationObserver {
  constructor(options = {}) {
    this.options = {
      rootMargin: options.rootMargin || '-10% 0px -20% 0px', // More forgiving trigger zone
      threshold: options.threshold || 0.1, // Simplified single threshold for better performance
      duration: options.duration || 600,
      easing: options.easing || 'ease-out',
      once: options.once !== false, // Default to true like AOS
      ...options
    };

    this.elements = new Map();
    this.observer = null;
    this.isReducedMotion = this.checkReducedMotion();

    this.init();
  }

  init() {
    // Respect user's motion preferences
    if (this.isReducedMotion) {
      this.showAllElements();
      return;
    }

    this.setupObserver();
    this.discoverElements();

    // Handle dynamic content
    this.setupMutationObserver();
  }

  checkReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  setupObserver() {
    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      this.useFallback();
      return;
    }

    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root: null,
        rootMargin: this.options.rootMargin,
        threshold: this.options.threshold
      }
    );
  }

  discoverElements() {
    // Find all elements with animation attributes
    const elements = document.querySelectorAll('[data-animate]');
    const viewportElements = [];

    elements.forEach((element, index) => {
      // Skip if already processed
      if (this.elements.has(element)) return;

      const config = this.parseElementConfig(element, index);
      this.elements.set(element, config);

      // Set initial state
      this.prepareElement(element, config);

      // Check if element is already in viewport on initial load
      const rect = element.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        // Collect viewport elements for batch processing
        viewportElements.push({ element, config });
      }

      // Start observing for future viewport entries
      if (this.observer) {
        this.observer.observe(element);
      }
    });

    // Batch animate all viewport elements with proper timing
    if (viewportElements.length > 0) {
      this.animateViewportBatch(viewportElements);
    }
  }

  animateViewportBatch(viewportElements) {
    // Use requestAnimationFrame for smooth batch animation
    requestAnimationFrame(() => {
      viewportElements.forEach(({ element, config }) => {
        // Apply animation with proper delay
        setTimeout(() => {
          this.triggerAnimation(element, config);
        }, config.delay);
      });
    });
  }

  parseElementConfig(element, index) {
    const animationType = element.dataset.animate || 'fade-in';
    const delay = this.parseDelay(element.dataset.animateDelay, index);
    const duration = parseInt(element.dataset.animateDuration) || this.options.duration;
    const easing = element.dataset.animateEasing || this.options.easing;
    const once = element.dataset.animateOnce !== 'false';

    return {
      type: animationType,
      delay,
      duration,
      easing,
      once,
      hasAnimated: false
    };
  }

  parseDelay(delayValue, index) {
    if (!delayValue) return 0;

    // Handle numeric delays (can be string from template)
    const numericDelay = parseInt(delayValue);
    if (!isNaN(numericDelay)) {
      return numericDelay;
    }

    // Handle 'stagger' keyword
    if (delayValue === 'stagger') {
      return index * 100; // 100ms stagger by default
    }

    return 0;
  }

  prepareElement(element, config) {
    // Add base classes
    element.classList.add('animate-element');
    element.classList.add(`animate-${config.type}`);

    // Set CSS variables for this element
    element.style.setProperty('--animate-duration', `${config.duration}ms`);
    element.style.setProperty('--animate-easing', config.easing);
    element.style.setProperty('--animate-delay', `${config.delay}ms`);
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      const element = entry.target;
      const config = this.elements.get(element);

      if (!config) return;

      if (entry.isIntersecting && entry.intersectionRatio > 0) {
        // Element is visible - only animate if not already animated
        if (!config.hasAnimated) {
          this.animateIn(element, config);
        }
      }
      // IMPORTANT: No animation-out behavior
      // Elements stay in their animated state permanently once they've animated in
      // This creates a one-way animation system where content reveals itself but never hides
    });
  }

  animateIn(element, config) {
    // Apply delay if specified
    if (config.delay > 0) {
      setTimeout(() => {
        this.triggerAnimation(element, config);
      }, config.delay);
    } else {
      this.triggerAnimation(element, config);
    }
  }

  triggerAnimation(element, config) {
    // Add both classes - animate-visible triggers the CSS transition
    element.classList.add('animate-visible'); // This triggers the CSS animation
    element.classList.add('animate-active');  // This enables will-change optimization
    config.hasAnimated = true;

    // Stop observing if animation should only play once
    if (config.once && this.observer) {
      this.observer.unobserve(element);
      // Clean up memory by removing from Map for one-time animations
      this.elements.delete(element);
    }

    // Clean up after animation completes
    setTimeout(() => {
      element.classList.add('animate-complete');
      // Remove will-change for performance
      element.style.willChange = 'auto';
    }, config.duration);
  }


  setupMutationObserver() {
    // Watch for dynamically added elements with optimized configuration
    const mutationObserver = new MutationObserver((mutations) => {
      // Check if any mutation actually added elements with animation attributes
      const hasNewAnimations = mutations.some(mutation => {
        if (mutation.type !== 'childList') return false;

        // Check added nodes for animation attributes
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && node.dataset?.animate) {
            return true;
          }
          // Check descendants if it's an element
          if (node.nodeType === 1 && node.querySelectorAll) {
            const animatedChildren = node.querySelectorAll('[data-animate]');
            if (animatedChildren.length > 0) return true;
          }
        }
        return false;
      });

      // Only re-scan if we actually found new animated elements
      if (hasNewAnimations) {
        this.discoverElements();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      // Only observe attribute changes for data-animate to reduce overhead
      attributes: false
    });
  }

  // Fallback for browsers without IntersectionObserver
  useFallback() {
    const checkElements = () => {
      const elements = document.querySelectorAll('[data-animate]');
      const windowHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementBottom = elementTop + rect.height;

        // Check if element is in viewport with offset
        const isVisible = (scrollTop + windowHeight > elementTop + 100) &&
                         (scrollTop < elementBottom);

        if (isVisible && !element.classList.contains('animate-visible')) {
          const config = this.parseElementConfig(element, index);
          this.prepareElement(element, config);
          this.animateIn(element, config);
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkElements();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Initial check
    checkElements();
  }

  showAllElements() {
    // For users who prefer reduced motion, show all elements immediately
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(element => {
      element.classList.add('animate-element', 'animate-visible', 'animate-active', 'animate-complete');
      element.style.transition = 'none';
    });
  }

  // Public API
  refresh() {
    this.discoverElements();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.elements.clear();
  }
}

// Initialize once content is revealed.
//
// The preloader gate keeps everything at `display:none` until `page-ready` is
// added to <body>. If we discover elements before then, getBoundingClientRect()
// returns zeros, so above-the-fold elements miss the in-viewport batch and are
// left to the IntersectionObserver — which can leave them stuck at opacity:0.
// Waiting for `page-ready` ensures discovery measures real geometry.
function whenContentReady(callback) {
  if (document.body && document.body.classList.contains('page-ready')) {
    callback();
    return;
  }

  var fired = false;
  function run() {
    if (fired) return;
    fired = true;
    callback();
  }

  // Watch for the preloader reveal adding `page-ready` to <body>.
  if (document.body) {
    var observer = new MutationObserver(function() {
      if (document.body.classList.contains('page-ready')) {
        observer.disconnect();
        run();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  // Fallback: never wait longer than the preloader's own fallback timeout.
  setTimeout(run, 2000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    whenContentReady(initAnimations);
  });
} else {
  whenContentReady(initAnimations);
}

function initAnimations() {
  // Initialize with configuration similar to AOS
  window.animationSystem = new AnimationObserver({
    rootMargin: '-50px 0px 0px 0px', // Trigger as soon as an element enters the viewport
    // threshold:0 — fire as soon as ANY part intersects. A non-zero threshold
    // breaks elements taller than the viewport (e.g. a long article's content
    // wrapper): they can never reach a 10% visibility ratio while scrolling past
    // their top, so they'd stay stuck at opacity:0.
    threshold: 0,
    duration: 600,
    easing: 'ease-out',
    once: true
  });
}

// Export for module usage
export default AnimationObserver;
export { initAnimations };